import splashScreenImage from './imgs/webpBG.webp';
import tapToStartImage from './imgs/tap_start.png';
import discordLoginImage from './imgs/discord_login.png';
import googleLogo from './imgs/g-logo.png';

import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../../services/auth/authProvider';
import { useApi } from '../../services/api/ApiProvider';
import { fetchShopItems } from '../../services/api/apiService';

import './style.css';
import { useEffect, useState, useRef } from 'react';

import LobbyManager from '../../features/lobby/managers/lobbyManager';
import DecksManager from '../../features/decks/managers/decksManager';
import StarterDeckSelectContainer from '../../features/lobby/containers/starterDeckSelectContainer';

const DISCORD_REDIRECT_URI = process.env.REACT_APP_DISCORD_REDIRECT_URI || (process.env.NODE_ENV === 'production' ? 'https://optcg.dev/auth/discord' : 'http://localhost:3001/auth/discord');
const GOOGLE_REDIRECT_URI = process.env.REACT_APP_GOOGLE_REDIRECT_URI || (process.env.NODE_ENV === 'production' ? 'https://optcg.dev/auth/google' : 'http://localhost:3001/auth/google');
const IS_LOCAL_BUILD = process.env.NODE_ENV === 'development';

const ERROR_MESSAGES = {
    'not_play_tester': (
        <div>You are not a play tester. To get access you can join our <a href="https://discord.gg/TRkBnW73Bu" target="_blank" rel="noreferrer">Discord server</a> and request access in the #play-testing channel.</div>
    )
}

export default function SplashScreen() {
    const navigate = useNavigate();
    const { isAuthenticated, isLoading, userName, isAdmin } = useAuth();
    const { decks, isLoadingDecks, isLoadingCollection } = useApi();
    const [error, setError] = useState(null);
    const [isLoadingLobby, setIsLoadingLobby] = useState(false);
    const googleCodeClient = useRef(null);

    useEffect(() => {
        const errorFromUrl = new URLSearchParams(window.location.search).get('error');
        if (errorFromUrl) {
            setError(ERROR_MESSAGES[errorFromUrl] || 'An unknown error occurred. Please try again later.');
        }
    }, []);

    const goToLobby = async () => {
        // show loading spinner
        setIsLoadingLobby(true);

        const starterDeckItemsPromise = (decks || []).length === 0
            ? fetchShopItems()
                .then((items) => (items || []).filter((item) => item.type === 'starter_deck'))
                .catch((error) => {
                    console.error('Failed to load starter decks:', error);
                    return [];
                })
            : Promise.resolve([]);

        // load lobby assets and, for a new player, the starter deck list + card art
        const [starterDeckItems] = await Promise.all([
            starterDeckItemsPromise.then(async (items) => {
                if (items.length > 0) {
                    await StarterDeckSelectContainer.loadItemAssets(items);
                }
                return items;
            }),
            LobbyManager.loadAssets(),
            DecksManager.loadStaticAssets(),
            DecksManager.loadDecksAssets(decks),
        ]);

        navigate('/lobby', {
            state: starterDeckItems.length > 0 ? { starterDeckItems } : undefined,
        });
    };

    const AdminDashboardButton = () => {
        return (
            <div>
                <button style={{ border: 0, color: 'white', backgroundColor: 'transparent', padding: '10px' }} onClick={() => navigate('/admin')}>
                    <span className="material-symbols-outlined" style={{ fontSize: '2rem' }}>settings</span>
                </button>
            </div>
        );
    };

    const startButton = () => {
        return (
            <>
                <img className="tap-to-start-text-image" src={tapToStartImage} alt="Tap to Start" />
                <div style={{ width: '100%', textAlign: 'center' }}>
                    <span className="change-username-text">Not {userName}? <a className="change-username-link" href="/auth/signout">Sign Out</a></span>
                </div>
            </>
        );
    };

    const TapToStartOverlay = () => {
        return (
            <button className="tap-to-start-overlay" onClick={goToLobby}></button>
        );
    };

    const DiscordLoginButton = () => {
        return (
            <a style={{ display: 'inline-block', width: '75%', backgroundColor: 'transparent', border: 0, pointerEvents: 'auto' }} href={`https://discord.com/oauth2/authorize?client_id=1442657888567820339&response_type=code&redirect_uri=${encodeURIComponent(DISCORD_REDIRECT_URI)}&scope=identify%20guilds.members.read`}>
                <img style={{ width: '100%' }} src={discordLoginImage} alt="Discord Login" />
            </a>
        );
    };

    const GoogleLoginButton = () => {
        return (
            <button className="google-signin-button" onClick={() => googleCodeClient.current.requestCode()}>
                <img className="google-signin-logo" src={googleLogo} alt="Google Logo" />
                <span className="google-signin-text">Sign in with Google</span>
            </button>
        );
    };

    const LoadingSpinner = () => {
        return (
            <div className="loading-spinner-container">
                <div className="loading-spinner" />
            </div>
        );
    }

    useEffect(() => {
        if (window.google?.accounts?.oauth2 && !googleCodeClient.current) {
            googleCodeClient.current = window.google.accounts.oauth2.initCodeClient({
                client_id: '707286922506-cccqiik13pi0lvre9hd2tctn29ps2f9r.apps.googleusercontent.com',
                scope: 'openid email profile',
                ux_mode: 'redirect',
                redirect_uri: GOOGLE_REDIRECT_URI,
            });
        }
    }, [isLoading]);

    return (
        <div className="splash-screen">
            <img style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', position: 'absolute', top: 0, left: 0 }} src={splashScreenImage} alt="Splash Screen" />
            { !isLoading && isAuthenticated && !isLoadingDecks && !isLoadingCollection && <TapToStartOverlay /> }
            { isAdmin && <div style={{ position: 'absolute', top: 0, right: 0 }}>
                <AdminDashboardButton />
            </div> }
            <div style={{ position: 'absolute', bottom: '20%', left: 0, width: '100%', textAlign: 'center', pointerEvents: 'none' }}>
                { 
                    !isLoading && (
                        isAuthenticated ?
                        !isLoadingDecks && !isLoadingCollection &&
                        startButton() :
                        <>
                            <GoogleLoginButton />
                            <DiscordLoginButton />
                            { IS_LOCAL_BUILD && <a className="dev-login-button" href="/auth/dev-admin">Dev Admin Login</a> }
                        </>
                    )
                }
            </div>
            { error && <div className="error-message">{error}</div> }
            { isLoadingLobby && <LoadingSpinner /> }
            {/* <div className="splash-screen__footer">
                <Link className="splash-screen__footer-link" to="/privacy">Privacy Policy</Link>
            </div> */}
        </div>
    );
}